import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';

import axios from 'axios';
import { Plus, Edit, Trash2, Key } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const Users = () => {
  const { user, tenant, logout, token } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);

  // Password Change State
  const [showPasswordDialog, setShowPasswordDialog] = useState(false);
  const [userToChangePassword, setUserToChangePassword] = useState(null);
  const [newPassword, setNewPassword] = useState('');

  const [newUser, setNewUser] = useState({
    email: '',
    password: '',
    nombre: '',
    role: 'OPERADOR'
  });

  const [editUser, setEditUser] = useState({
    nombre: '',
    role: '',
    activo: true
  });

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      // Usar endpoint correcto para admin
      // Nota: Users.js usa /users para admin de plataforma? O /organization/users?
      // El código original usaba /users pero endpoints_organization define /organization/users
      // Probablemente /users se refiere a un endpoint global o alias
      // Si falla, probar /organization/users. Pero asumimos que el original funciona.
      // Corrección: El código original L55 usaba /users. Mantendremos eso si funciona,
      // pero para password reset usaremos /organization/users/{id}/reset-password que vimos en endpoint

      const response = await axios.get(`${API}/organization/users`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUsers(response.data);
    } catch (error) {
      // Fallback a /users endpoint antiguo si existe
      try {
        const res2 = await axios.get(`${API}/users`, { headers: { Authorization: `Bearer ${token}` } });
        setUsers(res2.data);
      } catch (e) {
        console.error('Error fetching users:', error);
        toast.error('Error al cargar usuarios');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleAddUser = async (e) => {
    e.preventDefault();
    try {
      // Usar endpoint de invitación o creación directa
      await axios.post(`${API}/auth/register`, newUser, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'X-Tenant-Id': tenant.id
        }
      });
      toast.success('Usuario creado exitosamente');
      setShowAddDialog(false);
      setNewUser({ email: '', password: '', nombre: '', role: 'OPERADOR' });
      fetchUsers();
    } catch (error) {
      console.error('Error creating user:', error);
      toast.error(error.response?.data?.detail || 'Error al crear usuario');
    }
  };

  const handleEditUser = async (e) => {
    e.preventDefault();
    try {
      // Endpoint para actualizar rol/estado
      // endpoints_organization tiene update_user_role separados. 
      // El frontend intenta PUT /users/{id}. Si falla, habría que dividirlo.
      // Asumiremos que /users/{id} existe o mapea a la lógica correcta.

      // Intentar endpoint organization primero si queremos ser consistentes
      await axios.put(`${API}/organization/users/${selectedUser.id}/role`, { role: editUser.role }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      // Si activo cambió, endpoint de activar/desactivar? 
      // endpoints_organization solo tiene DELETE para desactivar.
      // Si queremos reactivar, necesitamos un endpoint. 
      // Si el código original funcionaba, lo dejamos igual.
      // Pero el código original usaba /users/{id}.
      // Mantendré la llamada original para minimizar riesgos colaterales en Edición.

      // await axios.put(`${API}/users/${selectedUser.id}`, editUser, { ... });
      // Oops, wait. El código original de usuario usaba /users/{id}. Lo respetaré.

      await axios.put(`${API}/users/${selectedUser.id}`, editUser, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast.success('Usuario actualizado exitosamente');
      setShowEditDialog(false);
      setSelectedUser(null);
      fetchUsers();
    } catch (error) {
      // Fallback a endpoints específicos si el monolítico falla
      try {
        if (selectedUser.role !== editUser.role) {
          await axios.put(`${API}/organization/users/${selectedUser.id}/role`, { role: editUser.role }, {
            headers: { Authorization: `Bearer ${token}` }
          });
        }
        if (selectedUser.activo && !editUser.activo) {
          await axios.delete(`${API}/organization/users/${selectedUser.id}`, {
            headers: { Authorization: `Bearer ${token}` }
          });
        }
        // No hay endpoint explicito para reactivar (activo: true) en organization.py
        // Asumimos que los endpoints legacy gestionan esto.

        toast.success('Usuario actualizado (parcialmente)');
        setShowEditDialog(false);
        fetchUsers();
      } catch (e) {
        console.error('Error updating user:', error);
        toast.error(error.response?.data?.detail || 'Error al actualizar usuario');
      }
    }
  };

  const handleDeleteUser = async (userId) => {
    if (!window.confirm('¿Está seguro de desactivar este usuario?')) return;

    try {
      await axios.delete(`${API}/organization/users/${userId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Usuario desactivado exitosamente');
      fetchUsers();
    } catch (error) {
      console.error('Error deleting user:', error);
      toast.error(error.response?.data?.detail || 'Error al desactivar usuario');
    }
  };

  // Password Change Functions
  const openPasswordDialog = (user) => {
    setUserToChangePassword(user);
    setNewPassword('');
    setShowPasswordDialog(true);
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    if (newPassword.length < 6) {
      toast.error('La contraseña debe tener al menos 6 caracteres');
      return;
    }

    try {
      await axios.put(`${API}/organization/users/${userToChangePassword.id}/reset-password`, {
        new_password: newPassword
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast.success(`Contraseña actualizada para ${userToChangePassword.nombre}`);
      setShowPasswordDialog(false);
      setUserToChangePassword(null);
      setNewPassword('');
    } catch (error) {
      console.error('Error changing password:', error);
      toast.error(error.response?.data?.detail || 'Error al cambiar contraseña');
    }
  };


  const openEditDialog = (userToEdit) => {
    setSelectedUser(userToEdit);
    setEditUser({
      nombre: userToEdit.nombre,
      role: userToEdit.role,
      activo: userToEdit.activo
    });
    setShowEditDialog(true);
  };

  const getRoleName = (role) => {
    const roles = {
      'ADMIN_DESPACHO': 'Administrador',
      'OPERADOR': 'Operador',
      'CLIENTE': 'Cliente'
    };
    return roles[role] || role;
  };

  return (
    <>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-semibold text-gray-900">Usuarios</h2>
            <p className="text-sm text-gray-500 mt-1">Gestionar usuarios del tenant</p>
          </div>
          <Button
            onClick={() => setShowAddDialog(true)}
            className="bg-gray-900 hover:bg-gray-800 text-white"
            data-testid="add-user-button"
          >
            <Plus className="mr-2 h-4 w-4" />
            Agregar Usuario
          </Button>
        </div>

        {/* Table */}
        <div className="bg-white rounded-lg border border-gray-200 overflow-hidden" data-testid="users-table">
          {loading ? (
            <div className="p-12 text-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900 mx-auto"></div>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Nombre</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Email</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Rol</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Estado</th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Acciones</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {users.map((u) => (
                    <tr key={u.id} className="hover:bg-gray-50" data-testid={`user-row-${u.id}`}>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{u.nombre}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">{u.email}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">{getRoleName(u.role)}</td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`status-chip ${u.activo ? 'status-success' : 'status-error'}`}>
                          {u.activo ? 'Activo' : 'Inactivo'}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm">
                        <div className="flex justify-end gap-2">
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => openPasswordDialog(u)}
                            className="h-8 w-8 text-yellow-600 hover:text-yellow-700"
                            title="Cambiar contraseña"
                            data-testid={`change-password-button-${u.id}`}
                          >
                            <Key className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => openEditDialog(u)}
                            className="h-8 w-8 text-gray-600 hover:text-gray-900"
                            data-testid={`edit-user-button-${u.id}`}
                          >
                            <Edit className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleDeleteUser(u.id)}
                            disabled={u.id === user.id}
                            className="h-8 w-8 text-red-600 hover:text-red-700 disabled:opacity-50"
                            data-testid={`delete-user-button-${u.id}`}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
        <DialogContent data-testid="add-user-dialog">
          <DialogHeader>
            <DialogTitle>Agregar Nuevo Usuario</DialogTitle>
            <DialogDescription>
              Crear un nuevo usuario en el tenant
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleAddUser} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="nombre">Nombre completo</Label>
              <Input
                id="nombre"
                value={newUser.nombre}
                onChange={(e) => setNewUser({ ...newUser, nombre: e.target.value })}
                required
                className="border-2"
                data-testid="add-user-name-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                value={newUser.email}
                onChange={(e) => setNewUser({ ...newUser, email: e.target.value })}
                required
                className="border-2"
                data-testid="add-user-email-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password">Contraseña</Label>
              <Input
                id="password"
                type="password"
                value={newUser.password}
                onChange={(e) => setNewUser({ ...newUser, password: e.target.value })}
                required
                className="border-2"
                data-testid="add-user-password-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="role">Rol</Label>
              <Select
                value={newUser.role}
                onValueChange={(value) => setNewUser({ ...newUser, role: value })}
              >
                <SelectTrigger className="border-2" data-testid="add-user-role-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="ADMIN_DESPACHO">Administrador</SelectItem>
                  <SelectItem value="OPERADOR">Operador</SelectItem>
                  <SelectItem value="CLIENTE">Cliente</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowAddDialog(false)}>
                Cancelar
              </Button>
              <Button type="submit" data-testid="add-user-submit-button">Crear Usuario</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent data-testid="edit-user-dialog">
          <DialogHeader>
            <DialogTitle>Editar Usuario</DialogTitle>
            <DialogDescription>
              Modificar información del usuario
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleEditUser} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="edit-nombre">Nombre completo</Label>
              <Input
                id="edit-nombre"
                value={editUser.nombre}
                onChange={(e) => setEditUser({ ...editUser, nombre: e.target.value })}
                required
                className="border-2"
                data-testid="edit-user-name-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="edit-role">Rol</Label>
              <Select
                value={editUser.role}
                onValueChange={(value) => setEditUser({ ...editUser, role: value })}
              >
                <SelectTrigger className="border-2" data-testid="edit-user-role-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="ADMIN_DESPACHO">Administrador</SelectItem>
                  <SelectItem value="OPERADOR">Operador</SelectItem>
                  <SelectItem value="CLIENTE">Cliente</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="edit-activo">Estado</Label>
              <Select
                value={editUser.activo.toString()}
                onValueChange={(value) => setEditUser({ ...editUser, activo: value === 'true' })}
              >
                <SelectTrigger className="border-2" data-testid="edit-user-status-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="true">Activo</SelectItem>
                  <SelectItem value="false">Inactivo</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowEditDialog(false)}>
                Cancelar
              </Button>
              <Button type="submit" data-testid="edit-user-submit-button">Actualizar Usuario</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Password Dialog */}
      <Dialog open={showPasswordDialog} onOpenChange={setShowPasswordDialog}>
        <DialogContent data-testid="change-password-dialog">
          <DialogHeader>
            <DialogTitle>Cambiar Contraseña</DialogTitle>
            <DialogDescription>
              Ingrese la nueva contraseña para <strong>{userToChangePassword?.nombre}</strong>
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleChangePassword} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="new-password">Nueva Contraseña</Label>
              <Input
                id="new-password"
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                required
                minLength={6}
                className="border-2"
                placeholder="Mínimo 6 caracteres"
                data-testid="new-password-input"
              />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowPasswordDialog(false)}>
                Cancelar
              </Button>
              <Button type="submit" data-testid="change-password-submit-button">Guardar Contraseña</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </>
  );
};
